import { useAtom, useSetAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router';
import { useState, useRef, useEffect } from 'react';

import {
  selectedPropertiesAtom,
  selectedPropertyIdsAtom,
  propertySearchOpenAtom,
} from '@/features/property-search';
import { PropertySearchButton } from './PropertySearchButton';
import { PropertySearchDropdown } from './PropertySearchDropdown';

import type { PropertySearchProps, Property } from '@/types/web/property';

export function PropertySearch({
  isHomePage,
  onNavigateToProperty,
  onSelectionChange,
}: PropertySearchProps) {
  const [open, setOpen] = useAtom(propertySearchOpenAtom);
  const [selectedProperties, setSelectedProperties] = useAtom(
    selectedPropertiesAtom,
  );
  const setSelectedPropertyIds = useSetAtom(selectedPropertyIdsAtom);
  const { propertyId: currentPropertyId } = useParams();

  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handlePropertySelect = (property: Property) => {
    if (isHomePage) {
      // Multi-selection for filtering on Home page
      const isSelected = selectedProperties.some((p) => p.id === property.id);

      let newSelection;
      if (isSelected) {
        newSelection = selectedProperties.filter((p) => p.id !== property.id);
      } else {
        newSelection = [...selectedProperties, property];
      }

      setSelectedProperties(newSelection);
      onSelectionChange?.(newSelection);
    } else {
      // Single selection for navigation on other pages
      if (
        selectedProperties.length === 1 &&
        selectedProperties[0].id === property.id
      ) {
        // If the same property is selected, navigate to it
        navigate(`/property/${property.id}`);
        onNavigateToProperty?.(property.id);
        setOpen(false);
      } else {
        // Single selection - replace any existing selection
        setSelectedProperties([property]);
        navigate(`/property/${property.id}`);
        onNavigateToProperty?.(property.id);
        setOpen(false);
      }
    }
  };

  useEffect(() => {
    // Select the property in the dropdown if it matches the URL parameter
    if (currentPropertyId) {
      setSelectedPropertyIds([currentPropertyId]);
    }
  }, [currentPropertyId, setSelectedPropertyIds]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, setOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <PropertySearchButton
        onSearchChange={setSearchTerm}
        isHomePage={isHomePage}
      />

      {open && (
        <PropertySearchDropdown
          searchTerm={searchTerm}
          isHomePage={isHomePage}
          onPropertySelect={handlePropertySelect}
        />
      )}
    </div>
  );
}
